/**
 * Layout y enrutado.
 *
 * Barra lateral fija + zona principal con la barra superior y la pantalla activa. La clave
 * `key={location.pathname}` en el contenedor de la pantalla es lo que hace que la animación de
 * entrada `naarFade` se dispare en cada navegación, igual que en el maquetado.
 *
 * FASE 2: la aplicación entera queda detrás de la sesión. Mientras se resuelve el refresco
 * silencioso NO se enseña el formulario de acceso: mostrarlo medio segundo a alguien que ya
 * tiene sesión es un parpadeo que parece un error.
 */
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { MobileTabBar } from './components/MobileTabBar';
import { ContractExpiryNotice } from './components/ContractExpiryNotice';
import { DashboardPage } from './pages/DashboardPage';
import { PropertiesPage } from './pages/PropertiesPage';
import { PropertyDetailPage } from './pages/PropertyDetailPage';
import { TenantsPage } from './pages/TenantsPage';
import { TenantDetailPage } from './pages/TenantDetailPage';
import { LedgerPage } from './pages/LedgerPage';
import { ConciliationPage } from './pages/ConciliationPage';
import { UtilitiesPage, UtilityDetailPage } from './pages/UtilitiesPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { PortalPage } from './pages/PortalPage';
import { SeguimientoPage, SEGUIMIENTO_TABS } from './pages/SeguimientoPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { AssistantPage } from './pages/AssistantPage';
import { LoginPage } from './pages/LoginPage';
import { useAuth } from './auth/AuthContext';
import { ToastViewport } from './components/feedback/Toaster';
import styles from './App.module.css';

function LoadingSession() {
  return (
    <div className={styles.booting} role="status" aria-live="polite">
      <span className={styles.bootingDot} />
      <span>Recuperando la sesión…</span>
    </div>
  );
}

export function App() {
  const location = useLocation();
  const { user, loading, role } = useAuth();
  /**
   * Navegación desplegada. Solo importa por debajo de 900px, donde la barra lateral pasa a ser
   * un panel sobre el contenido; en escritorio esta bandera no pinta nada.
   */
  const [navOpen, setNavOpen] = useState(false);

  // Cambiar de pantalla cierra el panel. Sin esto, en móvil se navega y el menú se queda
  // tapando la pantalla a la que acabas de llegar.
  useEffect(() => setNavOpen(false), [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setNavOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  if (loading) return <LoadingSession />;
  if (!user) return <LoginPage />;

  /**
   * El inquilino solo tiene portal. La API ya lo impone con un 403; esto evita que además vea
   * una barra lateral llena de enlaces que le van a rechazar, que es lo que hace que una
   * aplicación parezca rota aunque funcione correctamente.
   */
  if (role === 'tenant') {
    return (
      <div className={styles.shell}>
        <main className={styles.main}>
          {/* Sin barra lateral no hay tarjeta de cuenta, así que la sesión del inquilino no
              tenía forma de cerrarse. Va aquí, en la barra superior. */}
          <TopBar showAccount />
          <div className={styles.screen} key={location.pathname}>
            <Routes>
              <Route path="/portal" element={<PortalPage />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </Routes>
          </div>
        </main>
        <ToastViewport />
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <Sidebar open={navOpen} onNavigate={() => setNavOpen(false)} />
      {/* Velo del panel deslizante. Solo existe mientras está abierto y solo se ve por debajo
          de 900px; pulsarlo cierra, que es lo que todo el mundo intenta primero. */}
      {navOpen && (
        <button
          type="button"
          className={styles.navScrim}
          aria-label="Cerrar la navegación"
          onClick={() => setNavOpen(false)}
        />
      )}
      <main className={styles.main}>
        <TopBar onOpenNav={() => setNavOpen(true)} showAlerts />
        <div className={styles.screen} key={location.pathname}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/propiedades" element={<PropertiesPage />} />
            <Route path="/propiedades/:id" element={<PropertyDetailPage />} />
            <Route path="/inquilinos" element={<TenantsPage />} />
            <Route path="/inquilinos/:id" element={<TenantDetailPage />} />
            <Route path="/ingresos-y-gastos" element={<LedgerPage />} />
            <Route path="/ingresos-y-gastos/conciliacion" element={<ConciliationPage />} />
            <Route path="/suministros" element={<UtilitiesPage />} />
            <Route path="/suministros/:propertyId" element={<UtilityDetailPage />} />
            <Route path="/documentos" element={<DocumentsPage />} />
            <Route path="/seguimiento" element={<SeguimientoPage />} />
            <Route path="/asistente" element={<AssistantPage />} />
            {/* Pendientes vive en Seguimiento (y en la campana de arriba); las alertas, dentro de Pendientes. */}
            <Route path="/pendientes" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.pendientes}`} replace />} />
            <Route path="/alertas" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.pendientes}`} replace />} />
            {/*
              Las cinco secciones de consulta viven ahora como pestañas de Seguimiento. Sus
              direcciones de siempre redirigen a su pestaña: marcadores, enlaces de correos y
              el recorrido guiado siguen funcionando.
            */}
            <Route path="/cobros" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.cobros}`} replace />} />
            <Route path="/transacciones" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.movimientos}`} replace />} />
            <Route path="/mantenimiento" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.mantenimiento}`} replace />} />
            <Route path="/informes" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.informes}`} replace />} />
            <Route path="/portal" element={<Navigate to={`/seguimiento?tab=${SEGUIMIENTO_TABS.inquilino}`} replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </main>
      {/* En el teléfono, la navegación de uso diario va abajo, al alcance del pulgar. */}
      <MobileTabBar onOpenMore={() => setNavOpen(true)} moreOpen={navOpen} />
      {/* Al entrar: si hay contratos a menos de 3 meses de vencer, salta el aviso. */}
      <ContractExpiryNotice />
      <ToastViewport />
    </div>
  );
}
